import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    doppeHideableValueCreateHidden,
    doppeHideableValueIsVisibleAndNotEqualValue
} from '../../client-server-common/types/doppe-hideable-value';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import type {OmitStrict, PartiallyOptional, PartiallyRequired} from '@wix/devzai-utils-common';
import {arrayFlatten, arrayRemoveNullValues, asArray, immerProduce} from '@wix/devzai-utils-common';
import type {DoppeListActionSettings} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    DoppeActionLayout,
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetItemClickBehaviourDefaultListSettings,
    doppeListActionGetMainDetailsDefaultSettings,
    doppeListActionItemCtaButtonsResetSettingsForLock,
    doppeListActionListItemCalculateItemButtons,
    doppeListActionListItemGetDefaultListItemSettingsMigration,
    doppeListActionListItemWithCtaButtonsMigration,
    doppeListActionListItemWithSectionsMigration,
    doppeListActionSettingsFilterHiddenListItemsFromActionView,
    doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration,
    doppeListActionTemplateSectionsSettingsDefaultValuesMigration,
    doppeListActionWithMainDetailsGetMainImage,
    doppeListActionWithMainDetailsReplaceMainImage
} from '../../client-server-common';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import type {DoppeActionTypeTestimonial} from '../doppe-action-type-testimonial/doppe-action-type-testimonial';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';


export namespace DoppeActionTypeTestimonialsList {


    export interface TestimonialItemBaseSettings extends DoppeListActionSettings.ListItemBaseSettings,
        OmitStrict<DoppeActionTypeTestimonial.ActionSettings, 'sections' | 'ctaButtons'> {
    }

    export interface TestimonialItemSettings extends DoppeListActionSettings.ListItemWithSections,
        DoppeListActionSettings.ListItemWithCtaButtons,
        TestimonialItemBaseSettings {
    }

    export interface ActionSettings extends DoppeListActionSettings<DoppeActionTypeTestimonialsList.TestimonialItemSettings>,
        DoppeActionContentSectionsSettings,
        DoppeListActionSettings.WithSearchSettings,
        DoppeListActionSettings.WithMainDetails,
        DoppeActionCtaButtonsSettings,
        DoppeListActionSettings.WithItemClickBehaviour {
        itemShowRating: boolean;
        previewShowDescription: boolean;
    }

}

export function doppeActionTypeTestimonialsListResolveListItemSettings(testimonial: PartiallyRequired<DoppeActionTypeTestimonialsList.TestimonialItemSettings, 'id'>) {

    const {
        ctaButtonsOverridesMap,
        customCtaButtons,
        customSections,
        sectionsOverridesMap,
        id,
        deleted,
        displayInSearch,
        enabled,
        ...restProduct
    } = testimonial;

    return {
        ...doppeListActionListItemWithCtaButtonsMigration(testimonial, false),
        ...doppeListActionListItemWithSectionsMigration(testimonial),
        ...doppeListActionListItemGetDefaultListItemSettingsMigration(testimonial.id, testimonial),
        ...restProduct as PartiallyOptional<DoppeActionTypeTestimonialsList.TestimonialItemSettings, 'sectionsOverridesMap' | 'ctaButtonsOverridesMap' | 'customCtaButtons' | 'customSections'>
    }
}

export function doppeActionTypeTestimonialsListResolveActionSettings(actionSettings: Partial<DoppeActionTypeTestimonialsList.ActionSettings>) {
    const {
        sections,
        listDataItems = [],
        ctaButtons,
        listItemTemplateCtaButtonsSettings,
        listItemTemplateSectionsSettings,
        listSearchSettings,
        ...restSettings
    } = actionSettings;

    return {
        itemShowRating: true,
        previewShowTitle: true,
        previewShowDescription: true,
        previewShowAddress: true,
        previewShowPrice: true,
        ...doppeListActionGetItemClickBehaviourDefaultListSettings(),
        ...doppeListActionGetMainDetailsDefaultSettings(),
        ...doppeListActionGetDefaultSearchListSettings(actionSettings),
        ...doppeListActionGetDefaultListSettings({
            layout: DoppeActionLayout.Column,
            listDataItems: listDataItems.map(doppeActionTypeTestimonialsListResolveListItemSettings),
        }),
        ...doppeActionContentSectionSettingsDefaultValuesMigration(actionSettings),
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration(actionSettings, false),
        ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(actionSettings, false),
        ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(actionSettings),
        ...restSettings
    }
}

export const doppeActionTypeTestimonialsList = doppeActionTypeDefineMetadata<DoppeActionTypeTestimonialsList.ActionSettings>({
    id: 'co.hopp.action/testimonials-list',
    resolveActionSettings: action => {
        return doppeActionTypeTestimonialsListResolveActionSettings(action.settings);
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings);
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails()
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        }
    },
    actionListItemSettingsMetadata: {
        contentSettingsMetadata: {
            name: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            tagLine: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            profileMedia: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleImage, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            mainQuote: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon),
            date: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityPublishDate),
            rating: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Rating, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified)
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        }
    },
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isPaymentAction: false,
    isFormAction: false,
    isListItemLinkOpener: false,
    supportsListLayout: true,
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues([...arrayFlatten(actionSettings.listDataItems.map(itemSettings => {
            return itemSettings.profileMedia ? asArray(itemSettings.profileMedia) : []
        })), doppeHideableValueIsVisibleAndNotEqualValue(actionSettings.mainImage, null) ? actionSettings.mainImage : null]);
    },
    resolveMainMedia: actionSettings => {
        return doppeListActionWithMainDetailsGetMainImage(actionSettings, actionSettings.listDataItems[0]?.profileMedia ?? null);
    },
    resolveMainLink: (actionSettings, htmlSiteInfo, viewerLang, bymoPageProps, viewerPagesRoutesManager) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            htmlSiteInfo: htmlSiteInfo,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager
        }) ?? null;
    },
    resolveListItemsCount: actionSettings => {
        return doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.listDataItems).length;
    },
    resolveListItems: (action, htmlSiteInfo, viewerLang, bymoPageProps, viewerPagesRoutesManager) => {
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            return {
                url: doppeActionCtaButtonsResolveMainURL(
                    doppeListActionListItemCalculateItemButtons(actionSettings.listItemTemplateCtaButtonsSettings, item), {
                        htmlSiteInfo: htmlSiteInfo,
                        viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager
                    }) ?? null,
                title: item.name,
                action: action,
                couponCode: doppeHideableValueCreateHidden(''),
                description: item.description,
                image: item.profileMedia ? asArray(item.profileMedia)[0] : null,
                itemId: item.id,
                ribbon: item.ribbon,
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch
            }
        });
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            for (const item of actionSettings.listDataItems) {
                if (item.profileMedia) {
                    item.profileMedia = wixMediaResourceReplaceExternalImage(item.profileMedia, externalImagesUrlToImageResourceMap)
                }
            }

            doppeListActionWithMainDetailsReplaceMainImage(actionSettings, externalImagesUrlToImageResourceMap);
        })
    },
});
